import * as dateFns from "date-fns";
import { z } from "zod";
import { isValidDateString } from "@/packages/schemas/src/resources/common.schema";
import { CUSTOM_QUESTION_SIGNATURE_MAX_LENGTH, CUSTOM_QUESTION_TEXT_MAX_LENGTH, isPngDataUrl } from "@/packages/schemas/src/resources/customQuestion.schema";

export type CustomQuestionAnswerValue = string | number | boolean | null;
export type CustomQuestionAnswerMap = Record<string, CustomQuestionAnswerValue | undefined>;

export type ConditionalCustomQuestion = {
	key: string;
	enabled: boolean;
	condition?: {
		mode: "any" | "all";
		rules: Array<{
			questionKey: string;
			operator: "equals" | "notEquals";
			value?: CustomQuestionAnswerValue;
		}>;
	};
};

export type AnswerableCustomQuestion = ConditionalCustomQuestion & {
	id: string;
	label: string;
	description?: string;
	type: "checkbox" | "date" | "daterange" | "dropdown" | "number" | "signature" | "text" | "textarea";
	required: boolean;
	order: number;
	options?: Array<{ value: string; label: string }>;
};

export type CustomQuestionAnswer = {
	questionId: string;
	questionKey: string;
	value: CustomQuestionAnswerValue;
};

export type StoredCustomQuestionAnswer = {
	questionId: string;
	questionKey?: string;
	value?: CustomQuestionAnswerValue;
};

const LEGACY_DATE_FORMATS = ["dd/MM/yyyy", "yyyy/MM/dd", "dd-MM-yyyy", "dd.MM.yyyy", "yyyy.MM.dd"];

export function normalizeCustomQuestionDate(value: string): string {
	const canonicalDate = value.slice(0, 10);
	if (isValidDateString(canonicalDate)) return canonicalDate;

	for (const dateFormat of LEGACY_DATE_FORMATS) {
		const parsedDate = dateFns.parse(value, dateFormat, new Date(0));
		if (dateFns.isValid(parsedDate) && dateFns.format(parsedDate, dateFormat) === value) return dateFns.format(parsedDate, "yyyy-MM-dd");
	}

	return value;
}

function normalizeCustomQuestionDateRange(value: string): string {
	const [from, to, extra] = value.split("|");
	return extra === undefined && from && to ? `${normalizeCustomQuestionDate(from)}|${normalizeCustomQuestionDate(to)}` : value;
}

export function normalizeCustomQuestionAnswers<TAnswer extends StoredCustomQuestionAnswer>(
	answers: TAnswer[] = [],
	questions: Pick<AnswerableCustomQuestion, "id" | "key" | "type">[] = []
): TAnswer[] {
	const questionById = new Map(questions.map((question) => [question.id, question]));
	const questionByKey = new Map(questions.map((question) => [question.key, question]));

	return answers.map((answer) => {
		if (typeof answer.value !== "string") return answer;
		const question = questionByKey.get(answer.questionKey ?? "") ?? questionById.get(answer.questionId);
		if (question?.type === "date") return { ...answer, value: normalizeCustomQuestionDate(answer.value) };
		if (question?.type === "daterange") return { ...answer, value: normalizeCustomQuestionDateRange(answer.value) };
		return answer;
	});
}

export type CustomQuestionValidationOptions = {
	enforceRequired?: boolean;
};

export function filterCrmAnswerableQuestionConfig<TQuestion extends { fixedRole?: "fullName" | "signature" }, TConfig extends { questions: TQuestion[] }>(config: TConfig): TConfig {
	return {
		...config,
		questions: config.questions.filter(({ fixedRole }) => !fixedRole),
	};
}

const CustomQuestionAnswerValueSchema = z.union([z.string().max(CUSTOM_QUESTION_SIGNATURE_MAX_LENGTH), z.number().min(-1_000_000).max(1_000_000), z.boolean(), z.null()]);

export function isCustomQuestionVisible(question: ConditionalCustomQuestion, answers: CustomQuestionAnswerMap, questions: Pick<AnswerableCustomQuestion, "key" | "type">[] = []): boolean {
	if (!question.enabled) return false;
	if (!question.condition) return true;

	const matches = question.condition.rules.map((rule) => {
		const controllerType = questions.find(({ key }) => key === rule.questionKey)?.type;
		const answerValue = answers[rule.questionKey];
		const normalizeValue = (value: CustomQuestionAnswerValue | undefined) => {
			if (typeof value !== "string") return value;
			if (controllerType === "date") return normalizeCustomQuestionDate(value);
			if (controllerType === "daterange") return normalizeCustomQuestionDateRange(value);
			return value;
		};
		const equals = normalizeValue(answerValue) === normalizeValue(rule.value);
		return rule.operator === "equals" ? equals : !equals;
	});

	return question.condition.mode === "all" ? matches.every(Boolean) : matches.some(Boolean);
}

export function pruneHiddenCustomQuestionAnswers<TQuestion extends ConditionalCustomQuestion & Pick<AnswerableCustomQuestion, "type">>(
	questions: TQuestion[],
	answers: CustomQuestionAnswerMap
): CustomQuestionAnswerMap {
	const visibleAnswers = { ...answers };
	let changed = true;

	while (changed) {
		changed = false;
		for (const question of questions) {
			if (!isCustomQuestionVisible(question, visibleAnswers, questions) && Object.prototype.hasOwnProperty.call(visibleAnswers, question.key)) {
				delete visibleAnswers[question.key];
				changed = true;
			}
		}
	}

	return visibleAnswers;
}

export function getCustomQuestionAnswerMap(answers: StoredCustomQuestionAnswer[] = [], questions: Pick<AnswerableCustomQuestion, "id" | "key" | "type">[] = []): CustomQuestionAnswerMap {
	const questionKeyById = new Map(questions.map((question) => [question.id, question.key]));
	return normalizeCustomQuestionAnswers(answers, questions).reduce<CustomQuestionAnswerMap>((answerMap, answer) => {
		const questionKey = answer.questionKey ?? questionKeyById.get(answer.questionId);
		if (questionKey) answerMap[questionKey] = answer.value;
		return answerMap;
	}, {});
}

export function buildCustomQuestionAnswers<TQuestion extends AnswerableCustomQuestion>(questions: TQuestion[], answers: CustomQuestionAnswerMap): CustomQuestionAnswer[] {
	const visibleAnswers = pruneHiddenCustomQuestionAnswers(questions, answers);
	return [...questions]
		.filter((question) => isCustomQuestionVisible(question, visibleAnswers, questions) && visibleAnswers[question.key] !== undefined)
		.sort((left, right) => left.order - right.order)
		.map((question) => ({
			questionId: question.id,
			questionKey: question.key,
			value: visibleAnswers[question.key] ?? null,
		}));
}

function isEmptyAnswer(value: CustomQuestionAnswerValue | undefined): boolean {
	return value === undefined || value === null || value === "";
}

export function validateCustomQuestionAnswers<TQuestion extends AnswerableCustomQuestion>(
	questions: TQuestion[],
	answers: CustomQuestionAnswerMap,
	{ enforceRequired = true }: CustomQuestionValidationOptions = {}
): Record<string, string> {
	const visibleAnswers = pruneHiddenCustomQuestionAnswers(questions, answers);
	const errors: Record<string, string> = {};

	for (const question of [...questions].sort((left, right) => left.order - right.order)) {
		if (!isCustomQuestionVisible(question, visibleAnswers, questions)) continue;
		const value = visibleAnswers[question.key];

		if (enforceRequired && question.required && isEmptyAnswer(value)) {
			errors[question.key] = "This question is required.";
			continue;
		}
		if (isEmptyAnswer(value)) continue;

		if (question.type === "checkbox" && typeof value !== "boolean") errors[question.key] = "Select Yes or No.";
		else if (question.type === "number" && (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > 1_000_000))
			errors[question.key] = "Enter a valid number between -1,000,000 and 1,000,000.";
		else if (question.type === "dropdown" && (typeof value !== "string" || !question.options?.some((option) => option.value === value))) errors[question.key] = "Select a valid option.";
		else if (["text", "textarea", "date", "daterange", "signature"].includes(question.type) && typeof value !== "string") errors[question.key] = "Enter a valid answer.";
		else if (question.type === "signature" && typeof value === "string" && !isPngDataUrl(value)) errors[question.key] = "The signature is invalid. Clear it and try signing again.";
		else if (question.type !== "signature" && typeof value === "string" && value.length > CUSTOM_QUESTION_TEXT_MAX_LENGTH)
			errors[question.key] = `Enter no more than ${CUSTOM_QUESTION_TEXT_MAX_LENGTH} characters.`;
		else if (question.type === "date" && typeof value === "string" && !isValidDateString(value.slice(0, 10))) errors[question.key] = "Enter a valid date.";
		else if (question.type === "daterange" && typeof value === "string") {
			const [from, to, extra] = value.split("|");
			const normalizedFrom = from?.slice(0, 10);
			const normalizedTo = to?.slice(0, 10);
			if (extra !== undefined || !normalizedFrom || !normalizedTo || !isValidDateString(normalizedFrom) || !isValidDateString(normalizedTo) || normalizedFrom > normalizedTo) {
				errors[question.key] = "Enter a valid date range.";
			}
		}
	}

	return errors;
}

export function buildCustomQuestionAnswerSchema<TQuestion extends AnswerableCustomQuestion>(questions: TQuestion[], options?: CustomQuestionValidationOptions) {
	const shape = Object.fromEntries(questions.map((question) => [question.key, CustomQuestionAnswerValueSchema.optional()]));
	return z.object(shape).superRefine((answers, context) => {
		const errors = validateCustomQuestionAnswers(questions, answers, options);
		for (const [questionKey, message] of Object.entries(errors)) {
			context.addIssue({ code: "custom", path: [questionKey], message });
		}
	});
}
